// @flow
import moment from "moment"
import type { Moment } from "moment"
import * as R from "ramda"
import { differenceInDays, differenceInSeconds, differenceInMilliseconds } from "date-fns"

import { get, getOr } from "common/utils/get"
import set from "common/utils/set"
import JourneyProvider from "common/records/JourneyProvider"
import * as TripUtils from "common/records/Trip/utils"
import * as FlightUtils from "common/records/Flight/utils"
import type { Flight } from "common/records/Flight/Flight"
import type { PART_TYPE } from "booking/scenes/ReservationForm/consts/reservationConsts"
import { PART_TYPES } from "booking/scenes/ReservationForm/consts/reservationConsts"
import type { Trip, Stop } from "common/records/Trip/Trip"
import defaultJourney from "./Journey"
import type { Journey, Layover, Layovers, DifferentAirports } from "./Journey"
import type { JourneyAmenities } from "../../../booking/services/reservation/apis/journeyAmenitiesApi"
import airlineRestrictions from "../../../../data/airlineRestrictions.json" // eslint-disable-line import/no-restricted-paths
import type { SupportedJourneyHiddenTypes } from "common/services/journey/types"

// $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
export const createJourney = R.merge(defaultJourney)

export const getMidnight = (date: moment) => moment.utc(date).startOf("day")

// $FlowIssue
export const getFlights: Journey => Flight[] = R.compose(
  R.flatten,
  R.pluck("flights"),
  getOr([], "trips"),
)

export const getFlightById = (flightId: number): (Journey => ?Flight) =>
  R.compose(
    R.find(({ id }) => id === flightId),
    getFlights,
  )

export const getConnectingTripsFlights = (tripIndex: number, journey: Journey) => {
  const index = tripIndex || 0
  const outBoundTrip = journey.trips[index]
  const inboundTrip = journey.trips[index + 1]

  return {
    arrivalFlight: TripUtils.getArrival(outBoundTrip),
    departureFlight: inboundTrip && TripUtils.getDeparture(inboundTrip),
  }
}

// $FlowIssue
export const getFlightsIds: Journey => string[] = R.compose(
  R.pluck("id"),
  getFlights,
)

export const getId: Journey => string = R.compose(
  R.join("|"),
  getFlightsIds,
)

// $FlowIssue
export const getNaturalId: Journey => string = R.compose(
  R.join("|"),
  R.pluck("naturalId"),
  getFlights,
)

export const hasHiddenFlight: Journey => boolean = R.compose(
  R.any(getOr(false, "isHidden")),
  getFlights,
)

export const hasHiddenTrip: Journey => boolean = R.compose(
  R.any(TripUtils.isHidden),
  getOr([], "trips"),
)

export const getHiddenFlightsType: Journey => SupportedJourneyHiddenTypes = get("hiddenFlightsType")

export const getPrioCabinBagsFlights: Journey => Flight[] = R.compose(
  R.chain(TripUtils.getPrioCabinBagsFlights),
  get("trips"),
)

export const getPrice = (journey: Journey): ?number => get(["prices", "default"], journey)

export const getBestPrice = (journey: Journey): ?number =>
  R.ifElse(
    R.compose(
      R.isEmpty,
      get("providers"),
    ),
    getPrice,
    R.compose(
      get("price"),
      R.reduce(R.minBy(get("price")), { price: Infinity }),
      get("providers"),
    ),
  )(journey)

export const getTypes: Journey => PART_TYPE[] = R.compose(
  R.reduce((acc, trip) => R.union(acc, TripUtils.getTypes(trip)), []),
  get("trips"),
)

export const getOutboundTrip: Journey => ?Trip = R.compose(
  R.head,
  get("trips"),
)

export const isReturn: Journey => boolean = R.propEq("type", "return")

export const getDeparture = (journey: Journey) => {
  const outboundTrip = getOutboundTrip(journey)
  return outboundTrip && TripUtils.getDeparture(outboundTrip)
}

// if you need another units, create a new method
export const getTimeUntil = (journey: Journey) => {
  const departure = getDeparture(journey)
  return departure && differenceInSeconds(departure.when.utc, new Date())
}

export const getArrival = (journey: Journey) => {
  if (isReturn(journey)) {
    const outboundTrip = getOutboundTrip(journey)
    return outboundTrip && TripUtils.getArrival(outboundTrip)
  }
  const lastTrip = R.last(journey.trips)
  return lastTrip && TripUtils.getArrival(lastTrip)
}

export const getInboundTrip = (journey: Journey): ?Trip => get(["trips", 1], journey)

export const getDuration: Journey => Moment = R.compose(
  R.reduce((duration, trip) => duration.add(TripUtils.getDuration(trip)), moment.duration()),
  get("trips"),
)

export const getFlightsCount: Journey => number = R.compose(
  R.reduce((count, trip) => count + TripUtils.getFlightsCount(trip), 0),
  get("trips"),
)

export const isMulticity: Journey => boolean = R.propEq("type", "multicity")

export const getLayovers = (journey: Journey): Layovers => {
  let layovers = []
  const { trips } = journey
  for (let i = 1; i < trips.length; i += 1) {
    // Start from second flight
    const prevArrival = TripUtils.getArrival(trips[i - 1])
    const currDeparture = TripUtils.getDeparture(trips[i])
    const nightsInDestination = differenceInDays(
      getMidnight(currDeparture.when.local),
      getMidnight(prevArrival.when.local),
      "days",
    )

    if (!prevArrival || !currDeparture) return layovers

    const layover: Layover = {
      duration: differenceInMilliseconds(currDeparture.when.utc, prevArrival.when.utc),
      nightsInDestination,
      place: prevArrival.where,
      switchToPlace:
        prevArrival.where.code !== currDeparture.where.code ? currDeparture.where : null,
    }

    layovers = R.append(layover, layovers)
  }

  return layovers
}

// eslint-disable-next-line flowtype/no-weak-types
export const getCountries: Journey => Object[] = R.compose(
  R.reduce((agg, flight) => {
    ;["departure", "arrival"].forEach(x => {
      const country =
        typeof flight.getIn([x, "where", "country"]) !== "undefined"
          ? flight.getIn([x, "where", "country"]).toLowerCase()
          : null
      if (country && !agg.includes(country)) agg = R.append(country, agg) // eslint-disable-line no-param-reassign
    })
    return agg
  }, []),
  getFlights,
)

// eslint-disable-next-line flowtype/no-weak-types
export const getAirlines: Journey => Object[] = R.compose(
  R.reduce((agg, flight) => {
    const airline = flight.getIn(["airline", "code"])
    return agg.includes(airline) ? agg : R.append(airline, agg)
  }, []),
  getFlights,
)

// eslint-disable-next-line flowtype/no-weak-types
export const getAirports: Journey => Object[] = R.compose(
  R.reduce(
    (agg, flight) =>
      R.concat(agg, [
        flight.getIn(["arrival", "where", "code"]),
        flight.getIn(["departure", "where", "code"]),
      ]),
    [],
  ),
  getFlights,
)

export const getAirlinesWithNames: Journey => { [string]: string } = R.compose(
  R.reduce(
    (acc, flight) =>
      set([flight.getIn(["airline", "code"])], flight.getIn(["airline", "name"]), acc),
    {},
  ),
  getFlights,
)

export const getType: Journey => string = get("type")

export const getTripsDifferentAirports = (journey: Journey): DifferentAirports => {
  const { trips } = journey
  // Make sense only for journeys with more trips
  return trips.length > 1
    ? trips.reduce((acc, trip, index) => {
        const indexToCompare = index === 0 ? trips.length - 1 : index - 1
        // For first flight, compare with R.last... for R.any other, compare with previous
        const tripToCompare = trips[indexToCompare]

        const departure = TripUtils.getDeparture(trip)
        const arrival = TripUtils.getArrival(tripToCompare)
        const departureCode = departure && departure.where.code
        const arrivalCode = arrival && arrival.where.code
        const newData = { arrivalCode, departureCode }

        return arrivalCode && departureCode && arrivalCode !== departureCode
          ? set(index.toString(), newData, acc)
          : acc
      }, {})
    : {}
}

export const getFlightsDifferentAirports = (
  journeyType: ?string,
  journey: Journey,
): DifferentAirports => {
  const { trips } = journey

  return trips.reduce((acc, trip, index) => {
    let newAcc = acc
    const { flights } = trip
    // $FlowFixMe no idea
    newAcc = R.mergeDeepLeft(
      newAcc,
      flights.reduce((flightsAcc, flight, i) => {
        if (i === 0) {
          // Skip first flight
          return flightsAcc
        }

        const compareFlightIndex = i - 1
        const compareFlightArrival = get(["arrival", "where", "code"], flights[compareFlightIndex])
        const flightDeparture = get(["departure", "where", "code"], flight)

        return flightDeparture && compareFlightArrival && flightDeparture !== compareFlightArrival
          ? set(
              [index.toString(), flightDeparture.toLowerCase()],
              {
                arrival: compareFlightArrival,
                departure: flightDeparture,
                dataIdToHighlight: `${compareFlightIndex}-${compareFlightArrival.toLowerCase()}`,
              },
              flightsAcc,
            )
          : flightsAcc
      }, {}),
    )
    if (trips.length > 1) {
      const compareTripIndex = index === 0 ? trips.length - 1 : index - 1
      const compareTrip = trips[compareTripIndex]
      const departure = TripUtils.getDeparture(trip)
      const departureCode = departure && departure.where.code
      const arrival = TripUtils.getArrival(compareTrip)
      const arrivalCode = arrival && arrival.where.code

      // Some cases highlight departure airport for multicity
      if (index === 0 && (journeyType ? journeyType === "multicity" : getType(journey))) {
        return newAcc
      }
      if (arrivalCode && departureCode && departureCode !== arrivalCode) {
        const tripIndex = index === 0 ? trips.length - 1 : index
        const airportKey = index === 0 ? arrivalCode.toLowerCase() : departureCode.toLowerCase()
        const lastPart = index === 0
        newAcc = set(
          [tripIndex.toString(), airportKey],
          {
            arrival: arrivalCode,
            departure: departureCode,
            dataIdToHighlight:
              index === 0
                ? `0-${departureCode.toLowerCase()}`
                : `${compareTripIndex}-${arrivalCode.toLowerCase()}`,
            lastPart,
          },
          newAcc,
        )
      }
    }
    return newAcc
  }, {})
}

// $FlowFixMe
export const getStops: Journey => Stop[] = R.compose(
  R.chain(TripUtils.getStops),
  get("trips"),
)

export const hasShortLayover: Journey => boolean = R.compose(
  R.not,
  R.isEmpty,
  R.filter(duration => duration < 5 * 60 * 1000),
  R.chain(get("duration")),
  getStops,
)

export const hasNegativeLayover: Journey => boolean = R.compose(
  R.not,
  R.isEmpty,
  R.filter(duration => duration < 0),
  R.chain(get("duration")),
  getStops,
)

export const getStopovers = (journey: Journey) => {
  let stopovers = []
  const { trips } = journey
  trips.forEach(trip => {
    const { flights } = trip
    if (flights.length < 2) {
      return stopovers
    }
    flights.forEach(flight => {
      const arrivalWhere = get(["arrival", "where"], flight)
      const arrival = arrivalWhere && `${arrivalWhere.name} (${arrivalWhere.code})`
      stopovers = R.append(arrival, stopovers)
    })
    return null
  })
  return R.dropLast(1, stopovers)
}

export const getTopProvider: Journey => ?JourneyProvider = R.compose(
  R.head,
  R.sortBy(get("price")),
  get("providers"),
)

export const hasAirline: (string, Journey) => boolean = R.uncurryN(2, (airline: string) =>
  R.compose(
    R.reduce(
      (result, trip) =>
        result ||
        trip
          .get("flights")
          .reduce(
            (result2, flight) => result2 || flight.get("airline").get("code") === airline,
            result,
          ),
      false,
    ),
    get("trips"),
  ),
)

export const hasAirport: (string, Journey) => boolean = R.uncurryN(2, (code: string) =>
  R.compose(
    R.contains(code),
    getAirports,
  ),
)

export const hasBagsRecheck: Journey => boolean = R.compose(
  R.any(
    R.compose(
      Boolean,
      get("bagsRecheckRequired"),
    ),
  ),
  getFlights,
)

export const hasGuarantee: Journey => boolean = R.compose(
  R.any(get("guarantee")),
  getFlights,
)

export const hasFlight: Journey => boolean = R.compose(
  R.contains(PART_TYPES.FLIGHT),
  getTypes,
)

export const hasBus: Journey => boolean = R.compose(
  R.contains(PART_TYPES.BUS),
  getTypes,
)

export const hasTrain: Journey => boolean = R.compose(
  R.contains(PART_TYPES.TRAIN),
  getTypes,
)

export const isMultiModal: Journey => boolean = R.either(hasTrain, hasBus)

// return list of multicity cities names or ids in pairs { from: departure, to: destination }
export const getMulticityCityPairs = (cityId: boolean, journey: Journey) =>
  // $FlowFixMe
  journey.trips.map(trip => {
    const departure = TripUtils.getDeparture(trip)
    const arrival = TripUtils.getArrival(trip)
    return (
      departure &&
      arrival && {
        from: departure.where[cityId ? "city" : "name"],
        to: arrival.where[cityId ? "city" : "name"],
      }
    )
  })

//  returns list of multicity cities names or ids (all departure and arrival cities - with filtering duplicate entries in a row)
export const getMulticityCities = (cityId: boolean, journey: Journey): string[] => {
  const allstops = R.chain(pair => [pair.from, pair.to], getMulticityCityPairs(cityId, journey))
  const [first, ...rest] = allstops
  return [first, ...rest.filter((city, index) => city !== allstops[index])]
}

export const getNumberOfJourneyDays = (journey: Journey) => {
  const lastTrip = R.last(journey.trips)
  const lastPart = lastTrip && R.last(lastTrip.parts)
  const latestArrivalTime = get(["arrival", "when", "utc"], lastPart)

  const departure = getDeparture(journey)
  const firstDepartureTime = departure && departure.when.utc

  return latestArrivalTime && latestArrivalTime.diff(firstDepartureTime, "days")
}

export const endsInFuture = (journey: Journey) => {
  const lastFlight = R.compose(
    R.last,
    getFlights,
  )(journey)
  const arrival = get(["arrival", "when", "utc"], lastFlight)
  return arrival && arrival.isAfter(moment().utc())
}

export const getEndWhen = (journey: Journey) => {
  const lastFlight = R.compose(
    R.last,
    getFlights,
  )(journey)
  return get(["arrival", "when"], lastFlight)
}

export const hasFreePobedaBag: Journey => boolean = R.compose(
  R.equals(["DP"]),
  getAirlines,
)

export const hasPaidHandBagsAllowed: Journey => boolean = R.compose(
  R.equals(["F9"]),
  getAirlines,
)

export const hasPromocodeAllowed: Journey => boolean = R.compose(
  R.not,
  R.equals(["SU"]),
  getAirlines,
)

// $FlowIssue
export const hasTransfer: Journey => boolean = R.compose(
  R.lt(0),
  R.length,
  get("transfers"),
)

// Possible `what`s are fields in airlineRestrictions
// Default is true
export const hasAvailable = (what: string, journey: Journey): boolean =>
  R.compose(
    R.reduce((bool, airline) => bool && airline[what], true),
    R.filter(airline => hasAirline(airline.id, journey)),
    R.values,
  )(airlineRestrictions)

// $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
export const hasFlightWithinCountry = R.uncurryN(2, (country: string): (Journey => boolean) =>
  R.compose(
    R.any(FlightUtils.isFlightWithinCountry(country)),
    getFlights,
  ),
)

export const isTrainOnly: Journey => boolean = R.compose(
  R.equals(0),
  R.length,
  R.filter(item => item !== PART_TYPES.TRAIN),
  getTypes,
)

export const isBusOnly: Journey => boolean = R.compose(
  R.equals(0),
  R.length,
  R.filter(item => item !== PART_TYPES.BUS),
  getTypes,
)

export const isChangeAllowed = (hoursAllowedToChange: number): (Journey => boolean) =>
  R.compose(
    R.all(TripUtils.isChangeAllowed(hoursAllowedToChange)),
    get("trips"),
  )

export const isChangeAllowedForAnyTrip = (hoursAllowedToChange: number): (Journey => boolean) =>
  R.compose(
    R.any(TripUtils.isChangeAllowed(hoursAllowedToChange)),
    get("trips"),
  )

export const extendByAmenities = (
  journeyAmenities: JourneyAmenities,
  journey: Journey,
): Journey => {
  let _journeyAmenities = journeyAmenities

  const trips = journey.trips.map(trip => {
    // We have exactly same data in flights and parts for now
    // keep separated for future handling
    const flights = trip.flights.map((flight, i) => set("amenities", _journeyAmenities[i], flight))

    const parts = trip.parts.map((flight, i) => set("amenities", _journeyAmenities[i], flight))

    // shift used amenities and mutate
    _journeyAmenities = _journeyAmenities.slice(flights.length)
    return { flights, parts }
  })

  return set("trips", trips, journey)
}

// eslint-disable-next-line flowtype/no-weak-types
export const convertFromApi = (input: Object) => ({
  ...input,
  trips: input.trips.map(TripUtils.convertFromApi),
  providers: input.providers.map(JourneyProvider.fromPlainJS),
})

export const toStringLight = (journey: Journey) =>
  // eslint-disable-next-line flowtype/no-weak-types
  // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
  journey.getFlights().map<Object>(R.pick(["airline", "arrival", "departure"]))
